/**
 * Stimulus controller that disables an export form's submit button while a
 * file download is in flight, and re-enables it once the download has started
 * (signalled via a server-set cookie), the user returns to the tab, or a
 * timeout elapses.
 *
 * The form must include a hidden input named `download_token` (or one will be
 * created on submit). The backing controller action should set a cookie named
 * `download_ready_<token>` to `1` (path `/`, no httponly) immediately before
 * streaming the file. See `XlsxController#retailer_reports` and
 * `CsvController#products_and_items`.
 *
 * @example
 *   <%= form_with url: ..., method: :get, data: { controller: "export-form" } do |f| %>
 *     <%= f.button "Export",
 *           data: { export_form_target: "button" } %>
 *   <% end %>
 */
import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['button'];
  static values = {
    pollIntervalMs: { type: Number, default: 250 },
    timeoutMs: { type: Number, default: 60000 },
    busyText: { type: String, default: 'Exporting...' },
  };

  connect() {
    this.onSubmit = this.onSubmit.bind(this);
    this.onWindowFocus = this.onWindowFocus.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.element.addEventListener('submit', this.onSubmit);
  }

  disconnect() {
    this.element.removeEventListener('submit', this.onSubmit);
    this.cleanup();
  }

  onSubmit() {
    if (!this.hasButtonTarget) {
      return;
    }

    const token = this.generateToken();
    this.currentToken = token;
    this.setTokenInput(token);

    this.originalHtml = this.buttonTarget.innerHTML;
    this.buttonTarget.innerHTML = this.busyTextValue;
    this.buttonTarget.disabled = true;

    this.pollHandle = window.setInterval(() => {
      if (this.cookieReady(token)) {
        this.reenable();
      }
    }, this.pollIntervalMsValue);

    this.timeoutHandle = window.setTimeout(() => {
      this.reenable();
    }, this.timeoutMsValue);

    window.addEventListener('focus', this.onWindowFocus);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  onWindowFocus() {
    // When the user comes back to the tab after a "Save As" dialog, give the
    // cookie a brief moment to land then re-enable regardless.
    window.setTimeout(() => this.reenable(), 500);
  }

  onVisibilityChange() {
    if (document.visibilityState === 'visible') {
      window.setTimeout(() => this.reenable(), 500);
    }
  }

  reenable() {
    if (!this.hasButtonTarget || !this.currentToken) {
      return;
    }
    this.deleteCookie(`download_ready_${this.currentToken}`);
    this.buttonTarget.disabled = false;
    if (this.originalHtml !== undefined) {
      this.buttonTarget.innerHTML = this.originalHtml;
    }
    this.cleanup();
  }

  cleanup() {
    if (this.pollHandle) {
      window.clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
    if (this.timeoutHandle) {
      window.clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }
    window.removeEventListener('focus', this.onWindowFocus);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.currentToken = null;
    this.originalHtml = undefined;
  }

  cookieReady(token) {
    const name = `download_ready_${token}=`;
    return document.cookie.split(';').some((c) => c.trim().startsWith(name));
  }

  deleteCookie(name) {
    document.cookie = `${name}=; Max-Age=0; path=/`;
  }

  setTokenInput(token) {
    let input = this.element.querySelector('input[name="download_token"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'download_token';
      this.element.appendChild(input);
    }
    input.value = token;
  }

  generateToken() {
    if (window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}
